import { Crop, RotateCcw, Video } from "lucide-react";
import { CAMERA_MOVES } from "@/lib/motion";

const RATIOS = [["1:1", 1], ["9:16", 9 / 16], ["16:9", 16 / 9], ["4:5", 4 / 5]];
const pct = (v) => `${Math.round((v || 0) * 100)}%`;

// Centred crop that turns a vw×vh frame into the target aspect ratio.
const ratioCrop = (ratio, vw, vh) => {
  const src = vw / vh;
  if (Math.abs(src - ratio) < 0.01) return { l: 0, t: 0, r: 0, b: 0 };
  if (src > ratio) { const keep = ratio / src, m = (1 - keep) / 2; return { l: m, r: m, t: 0, b: 0 }; }
  const keep = src / ratio, m = (1 - keep) / 2;
  return { l: 0, r: 0, t: m, b: m };
};

// Properties-panel crop (sliders + ratio presets + drag on preview) and opacity for a video clip.
export const VideoCropOpacity = ({ clip, meta, onPatch, onCropOnPreview, cropEditing }) => {
  const c = { l: 0, t: 0, r: 0, b: 0, ...(clip.crop || {}) };
  const vw = meta?.width || 1920, vh = meta?.height || 1080;
  const setC = (k, v) => onPatch({ crop: { ...c, [k]: Math.max(0, Math.min(0.9 - (c[{ l: "r", r: "l", t: "b", b: "t" }[k]] || 0), v)) } });
  const op = clip.opacity ?? 1;
  const slider = (k, label) => (
    <label key={k} className="flex items-center gap-1 text-xs text-[#A1A1AA]">{label}
      <input type="range" min="0" max="0.45" step="0.005" value={c[k]} data-testid={`video-crop-${k}`} onChange={(e) => setC(k, +e.target.value)} className="w-16 accent-[#00F0FF]" />
      <span className="w-8 tabular-nums text-white">{pct(c[k])}</span></label>
  );
  return (
    <div className="flex flex-wrap items-center gap-2 border-l border-white/10 pl-2" data-testid="video-crop-opacity">
      <Crop size={13} className="text-[#00F0FF]" />
      <span className="text-xs text-[#A1A1AA]">Crop</span>
      {RATIOS.map(([l, r]) => (
        <button key={l} onClick={() => onPatch({ crop: ratioCrop(r, vw, vh) })} data-testid={`video-crop-ratio-${l.replace(":", "x")}`}
          className="rounded-md border border-white/15 px-1.5 py-0.5 text-[11px] font-semibold text-[#A1A1AA] hover:border-[#00F0FF]/60 hover:text-[#00F0FF]">{l}</button>
      ))}
      {slider("l", "Left")}{slider("r", "Right")}{slider("t", "Top")}{slider("b", "Bottom")}
      {onCropOnPreview && (
        <button onClick={onCropOnPreview} disabled={cropEditing} data-testid="video-crop-on-preview"
          className="rounded-lg border border-[#00F0FF]/50 px-2 py-1 text-xs font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-50">{cropEditing ? "Cropping…" : "Drag on preview"}</button>
      )}
      {(c.l || c.r || c.t || c.b) ? <button onClick={() => onPatch({ crop: null })} data-testid="video-crop-reset" title="Remove crop" className="rounded p-1 text-[#A1A1AA] hover:text-white"><RotateCcw size={12} /></button> : null}
      <span className="flex items-center gap-1 border-l border-white/10 pl-2 text-xs text-[#A1A1AA]"><Video size={12} className="text-[#30D158]" /> Camera
        <select value={clip.camera?.type || "none"} data-testid="video-camera-move"
          onChange={(e) => onPatch({ camera: e.target.value === "none" ? null : { intensity: 0.6, ...(clip.camera || {}), type: e.target.value } })}
          className="rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-xs text-white outline-none">
          {CAMERA_MOVES.map(([v, l]) => <option key={v} value={v} className="bg-[#141414]">{l}</option>)}
        </select>
        {clip.camera?.type && <input type="range" min="0" max="1" step="0.05" value={clip.camera.intensity ?? 0.6} data-testid="video-camera-intensity"
          onChange={(e) => onPatch({ camera: { ...clip.camera, intensity: +e.target.value } })} className="w-14 accent-[#30D158]" />}
      </span>
      <label className="flex items-center gap-1 border-l border-white/10 pl-2 text-xs text-[#A1A1AA]">Opacity
        <input type="range" min="0" max="1" step="0.05" value={op} data-testid="video-opacity" onChange={(e) => onPatch({ opacity: +e.target.value })} className="w-16 accent-[#00F0FF]" />
        <span className="w-8 tabular-nums text-white">{pct(op)}</span></label>
    </div>
  );
};
